import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';

import { PositionAggregation } from '@models/position/positionAggregation.class';
import { PortfolioTotals } from '@models/position/positionSummary.class';
import { ValueTypeAggregation } from '@models/position/valueTypeAggregation.class';
import { AppState } from 'src/app';
import { PositionValueTypes } from 'src/app/app.constants';
import { BootstrapService } from 'src/core/services/bootstrap.service';
import { MarketType, SharedSolaceStreamService } from 'src/solace';

@Injectable()
export class PositionService {
  private totalMarketValue: number;
  private operationTime: any;

  private valueTypes: any = {
    DebtInvesmentSocieties: 5,
    Cash: 27,
    Total: 1000,
  };

  private virtualValueType = 999;

  constructor(
    private store: Store<AppState>,
    private bootstrapService: BootstrapService,
    private sharedSolaceStreamService: SharedSolaceStreamService,
  ) {
    this.init();
  }

  updatePosition(data: any[]): any {
    let valueType: any;
    let solaceSymbol: any;
    let aggValue: any;

    const position: any = {
      positionDetail: [],
    };
    position.positionDetail.push.apply(position.positionDetail, data);

    const totalValuePos: any = this.filterPositionByValueType(
      this.valueTypes.Total,
      position,
    );

    this.totalMarketValue = totalValuePos
      ? totalValuePos.marketValue
      : undefined;

    const aggPosition: any = {
      aggPositionDetail: [],
    };

    for (let i = 0, len = position.positionDetail.length; i < len; i++) {
      const value: any = position.positionDetail[i];

      valueType = this.filterPositionValueType(
        aggPosition,
        value.positionValueType,
      );
      let valueTypeLabel: string;

      if (!valueType) {
        valueTypeLabel = this.filterValueTypeLables(value.positionValueType);
        valueType = new ValueTypeAggregation(
          value.positionValueType,
          valueTypeLabel,
        );
        aggPosition.aggPositionDetail.push(valueType);
      }

      // if nac or sic position get solace symbol
      if (
        value.instrument.instrumentType === MarketType.BMV ||
        value.instrument.instrumentType === MarketType.SIC
      ) {
        const marketName: string =
          value.instrument.instrumentType === MarketType.BMV
            ? 'marketBMV'
            : 'marketSIC';

        solaceSymbol = this.sharedSolaceStreamService.getMarketElement(
          marketName,
          value.instrument.issueID,
        );
        aggValue = new PositionAggregation(
          value,
          this.totalMarketValue,
          this.operationTime,
          solaceSymbol,
        );
      } else {
        aggValue = new PositionAggregation(
          value,
          this.totalMarketValue,
          this.operationTime,
        );
      }

      valueType.valueTypeDetail.push(aggValue);

      // proces virtualValueTypeAgg
      if (value.positionValueType === 27 || value.positionValueType === 150) {
        let virtualPos: any = aggPosition.aggPositionDetail.filter(
          (item: any) => {
            return item.valueType === this.virtualValueType; // place all the cash info on the same virtual value type
          },
        );

        if (virtualPos.length === 0) {
          virtualPos = new ValueTypeAggregation(
            this.virtualValueType,
            valueTypeLabel,
          );
          aggPosition.aggPositionDetail.push(virtualPos);
        } else {
          virtualPos = virtualPos[0];
        }

        const pos: any[] = virtualPos.valueTypeDetail.filter((item: any) => {
          return item.position.instrument.issueID === value.instrument.issueID;
        });

        if (!pos || pos.length === 0) {
          aggValue = new PositionAggregation(
            value,
            this.totalMarketValue,
            this.operationTime,
          );

          virtualPos.valueTypeDetail.push(aggValue);
        } else {
          pos[0].position = value;
        }
      }
    }

    aggPosition.aggPositionDetail.sort((a: any, b: any) => {
      return a.valueType > b.valueType;
    });

    for (
      let key = 0, len = aggPosition.aggPositionDetail.length;
      key < len;
      key++
    ) {
      aggPosition.aggPositionDetail[key].processAggData();
    }

    return {
      position,
      aggPosition,
    };
  }

  getTotals(
    aggPosition: any,
    capitalMarketRisks: any,
    margin: number,
    sellingPower: number,
    buyingPower: number,
  ): any {
    //  console.log('getTotals  aggPosition: %o', aggPosition);
    let total: any = this.filterPositionValueType(
      aggPosition,
      this.valueTypes.Total,
    );
    total = total ? total : { marketValueSubTot: 0 };

    const gbmf2Result = this.filterPositionValueType(
      aggPosition,
      this.valueTypes.DebtInvesmentSocieties,
    );

    let gbmf2 = 0;

    if (gbmf2Result) {
      for (const element of gbmf2Result.valueTypeDetail) {
        if (
          element.position &&
          element.position.instrument &&
          element.position.instrument.symbol === 'GBMF2'
        ) {
          gbmf2 +=
            element.position.instrument.lastPrice * element.position.shares;
        }
      }
    }

    const cashResult: any = this.filterPositionValueType(
      aggPosition,
      this.valueTypes.Cash,
    );
    const cash: number = cashResult ? cashResult.marketValueSubTot : 0;
    // console.log('cashResult: %o', cashResult);

    // buyingPower = cash + gbmf2 - capitalMarketRisks.virtualSalesGBMF2 + capitalMarketRisks.reportos - capitalMarketRisks.pendingOrdersRisk;

    return {
      total,
      gbmf2,
      cash,
      capitalMarketRisks,
      buyingPower,
      margin,
      sellingPower,
    };
  }

  getTotalsResume(
    position: any,
    capitalMarketRisks: any,
    margin: number,
    sellingPower: number,
    buyingPowerData: any,
  ): PortfolioTotals {
    const buyingPower = buyingPowerData ? buyingPowerData.buyingPower : 0;
    const buyingPowerSecureAmount = buyingPowerData
      ? buyingPowerData.buyingPowerSecureAmount
      : 0;
    const buyingPowerMarginAmount = buyingPowerData
      ? buyingPowerData.buyingPowerMarginAmount
      : 0;

    let total: any = buyingPowerData ? buyingPowerData.marketValueTotal : 0;
    total = total ? { marketValue: total } : { marketValue: 0 };

    const gbmf2Result = position.sociedadesInversionDeuda;
    let gbmf2 = 0;

    if (gbmf2Result) {
      const subTotal = this.filterPositionTypeTotals(gbmf2Result);
      gbmf2 = subTotal.marketValue;
    }

    const cashResult: any = position.efectivo;
    const subTotalEfectivo =
      cashResult !== undefined ? this.filterPositionTypeTotals(cashResult) : 0;
    const cash: number = subTotalEfectivo ? subTotalEfectivo.marketValue : 0;

    return {
      total,
      gbmf2,
      cash,
      capitalMarketRisks,
      buyingPower,
      margin,
      sellingPower,
      buyingPowerMarginAmount,
      buyingPowerSecureAmount,
    };
  }

  private init(): void {
    this.bootstrapService.initSession().then(() => {
      this.store
        .select('marketOperationTime')
        .pipe(
          map((value: any) => value.toJS()),
          take(1),
        )
        .subscribe((value: any) => {
          this.operationTime = value;
        });
    });
  }

  private filterPositionByValueType(valueType: number, position: any): any {
    let filtered: any;

    if (position) {
      filtered = position.positionDetail.filter((value: any, index: any) => {
        return value.positionValueType === valueType;
      });

      if (filtered && filtered.length > 0) {
        filtered = filtered[0];
      }
    }

    return filtered;
  }

  private filterPositionValueType(aggPosition: any, valueType: number): any {
    const valueTypes: any[] = aggPosition.aggPositionDetail;
    let result: any;

    // search for existing vlue types
    const pos: any[] = valueTypes.filter((item: any) => {
      return item.valueType === valueType;
    });

    if (pos && pos.length > 0) {
      result = pos[0];
    }

    return result;
  }

  private filterPositionTypeTotals(position: any): any {
    const valueTypes: any[] = position;
    let result: any;

    const pos: any[] = valueTypes.filter((item: any) => {
      return item.issueId === 'Subtotal';
    });

    if (pos && pos.length > 0) {
      result = pos[0];
    }
    return result;
  }

  private filterValueTypeLables(valueType: number): string {
    let result: string;

    const lbl: any[] = PositionValueTypes.filter(function filterLable(item) {
      return item.id === valueType;
    });

    if (lbl && lbl.length > 0) {
      result = lbl[0].name;
    } else {
      result = `VALUE TYPE ${valueType}`;
    }

    return result;
  }
}
